import { getTitleEscrowAddress, encrypt, isTitleEscrowVersion, TitleEscrowInterface } from '../core';
import { v4Contracts } from '../token-registry-v4';
import { v5Contracts } from '../token-registry-v5';
import { getTxOptions } from './utils';
import { getEthersContractFromProvider, isV6EthersProvider } from '../utils/ethers';

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
const transferHolder = /* @__PURE__ */ __name(async (contractOptions, signer, params, options) => {
  const { tokenRegistryAddress, tokenId } = contractOptions;
  const { titleEscrowVersion } = options;
  let { titleEscrowAddress } = contractOptions;
  const { chainId, maxFeePerGas, maxPriorityFeePerGas } = options;
  let isV5TT = titleEscrowVersion === "v5";
  let isV4TT = titleEscrowVersion === "v4";
  if (!titleEscrowAddress) {
    if (!tokenRegistryAddress) throw new Error("Token registry address is required");
    if (!tokenId) throw new Error("Token ID is required");
    titleEscrowAddress = await getTitleEscrowAddress(
      tokenRegistryAddress,
      tokenId,
      signer.provider,
      { titleEscrowVersion }
    );
  }
  if (!titleEscrowAddress) throw new Error("Token registry address is required");
  if (!signer.provider) throw new Error("Provider is required");
  const { holderAddress, remarks } = params;
  const encryptedRemarks = remarks ? `0x${encrypt(remarks, options.id)}` : "0x";
  if (titleEscrowVersion === void 0) {
    [isV4TT, isV5TT] = await Promise.all([
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V4,
        provider: signer.provider
      }),
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V5,
        provider: signer.provider
      })
    ]);
  }
  if (!isV4TT && !isV5TT) {
    throw new Error("Only Token Registry V4/V5 is supported");
  }
  const Contract = getEthersContractFromProvider(signer.provider);
  let titleEscrowContract;
  if (isV5TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v5Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  } else if (isV4TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v4Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  }
  try {
    const isV6 = isV6EthersProvider(signer.provider);
    const args = isV5TT ? [holderAddress, encryptedRemarks] : [holderAddress];
    if (isV6) {
      await titleEscrowContract.transferHolder.staticCall(...args);
    } else {
      await titleEscrowContract.callStatic.transferHolder(...args);
    }
  } catch (e) {
    console.error("callStatic failed:", e);
    throw new Error("Pre-check (callStatic) for transferHolder failed");
  }
  const txOptions = await getTxOptions(signer, chainId, maxFeePerGas, maxPriorityFeePerGas);
  if (isV5TT) {
    return await titleEscrowContract.transferHolder(holderAddress, encryptedRemarks, txOptions);
  } else if (isV4TT) {
    return await titleEscrowContract.transferHolder(holderAddress, txOptions);
  }
}, "transferHolder");
const transferBeneficiary = /* @__PURE__ */ __name(async (contractOptions, signer, params, options) => {
  const { tokenId, tokenRegistryAddress } = contractOptions;
  const { titleEscrowVersion } = options;
  let { titleEscrowAddress } = contractOptions;
  const { chainId, maxFeePerGas, maxPriorityFeePerGas } = options;
  let isV5TT = titleEscrowVersion === "v5";
  let isV4TT = titleEscrowVersion === "v4";
  if (!titleEscrowAddress) {
    titleEscrowAddress = await getTitleEscrowAddress(
      tokenRegistryAddress,
      tokenId,
      signer.provider,
      { titleEscrowVersion }
    );
  }
  if (!titleEscrowAddress) throw new Error("Token registry address is required");
  if (!signer.provider) throw new Error("Provider is required");
  const { newBeneficiaryAddress, remarks } = params;
  const encryptedRemarks = remarks ? `0x${encrypt(remarks, options.id)}` : "0x";
  if (titleEscrowVersion === void 0) {
    [isV4TT, isV5TT] = await Promise.all([
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V4,
        provider: signer.provider
      }),
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V5,
        provider: signer.provider
      })
    ]);
  }
  if (!isV4TT && !isV5TT) {
    throw new Error("Only Token Registry V4/V5 is supported");
  }
  const Contract = getEthersContractFromProvider(signer.provider);
  let titleEscrowContract;
  if (isV5TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v5Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  } else if (isV4TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v4Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  }
  try {
    const isV6 = isV6EthersProvider(signer.provider);
    const args = isV5TT ? [newBeneficiaryAddress, encryptedRemarks] : [newBeneficiaryAddress];
    if (isV6) {
      await titleEscrowContract.transferBeneficiary.staticCall(...args);
    } else {
      await titleEscrowContract.callStatic.transferBeneficiary(...args);
    }
  } catch (e) {
    console.error("callStatic failed:", e);
    throw new Error("Pre-check (callStatic) for transferBeneficiary failed");
  }
  const txOptions = await getTxOptions(signer, chainId, maxFeePerGas, maxPriorityFeePerGas);
  if (isV5TT) {
    return await titleEscrowContract.transferBeneficiary(
      newBeneficiaryAddress,
      encryptedRemarks,
      txOptions
    );
  } else if (isV4TT) {
    return await titleEscrowContract.transferBeneficiary(newBeneficiaryAddress, txOptions);
  }
}, "transferBeneficiary");
const transferOwners = /* @__PURE__ */ __name(async (contractOptions, signer, params, options) => {
  const { tokenId, tokenRegistryAddress } = contractOptions;
  const { titleEscrowVersion } = options;
  let { titleEscrowAddress } = contractOptions;
  const { chainId, maxFeePerGas, maxPriorityFeePerGas } = options;
  let isV5TT = titleEscrowVersion === "v5";
  let isV4TT = titleEscrowVersion === "v4";
  if (!titleEscrowAddress) {
    titleEscrowAddress = await getTitleEscrowAddress(
      tokenRegistryAddress,
      tokenId,
      signer.provider,
      { titleEscrowVersion }
    );
  }
  if (!titleEscrowAddress) throw new Error("Token registry address is required");
  if (!signer.provider) throw new Error("Provider is required");
  const { newBeneficiaryAddress, newHolderAddress, remarks } = params;
  const encryptedRemarks = remarks ? `0x${encrypt(remarks, options.id)}` : "0x";
  if (titleEscrowVersion === void 0) {
    [isV4TT, isV5TT] = await Promise.all([
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V4,
        provider: signer.provider
      }),
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V5,
        provider: signer.provider
      })
    ]);
  }
  if (!isV4TT && !isV5TT) {
    throw new Error("Only Token Registry V4/V5 is supported");
  }
  const Contract = getEthersContractFromProvider(signer.provider);
  let titleEscrowContract;
  if (isV5TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v5Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  } else if (isV4TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v4Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  }
  try {
    const isV6 = isV6EthersProvider(signer.provider);
    const args = isV5TT ? [newBeneficiaryAddress, newHolderAddress, encryptedRemarks] : [newBeneficiaryAddress, newHolderAddress];
    if (isV6) {
      await titleEscrowContract.transferOwners.staticCall(...args);
    } else {
      await titleEscrowContract.callStatic.transferOwners(...args);
    }
  } catch (e) {
    console.error("callStatic failed:", e);
    throw new Error("Pre-check (callStatic) for transferOwners failed");
  }
  const txOptions = await getTxOptions(signer, chainId, maxFeePerGas, maxPriorityFeePerGas);
  if (isV5TT) {
    return await titleEscrowContract.transferOwners(
      newBeneficiaryAddress,
      newHolderAddress,
      encryptedRemarks,
      txOptions
    );
  } else if (isV4TT) {
    return await titleEscrowContract.transferOwners(
      newBeneficiaryAddress,
      newHolderAddress,
      txOptions
    );
  }
}, "transferOwners");
const nominate = /* @__PURE__ */ __name(async (contractOptions, signer, params, options) => {
  const { tokenId, tokenRegistryAddress } = contractOptions;
  const { titleEscrowVersion } = options;
  let { titleEscrowAddress } = contractOptions;
  const { chainId, maxFeePerGas, maxPriorityFeePerGas } = options;
  let isV5TT = titleEscrowVersion === "v5";
  let isV4TT = titleEscrowVersion === "v4";
  if (!titleEscrowAddress) {
    titleEscrowAddress = await getTitleEscrowAddress(
      tokenRegistryAddress,
      tokenId,
      signer.provider,
      { titleEscrowVersion }
    );
  }
  if (!titleEscrowAddress) throw new Error("Token registry address is required");
  if (!signer.provider) throw new Error("Provider is required");
  const { newBeneficiaryAddress, remarks } = params;
  const encryptedRemarks = remarks ? `0x${encrypt(remarks, options.id)}` : "0x";
  if (titleEscrowVersion === void 0) {
    [isV4TT, isV5TT] = await Promise.all([
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V4,
        provider: signer.provider
      }),
      isTitleEscrowVersion({
        titleEscrowAddress,
        versionInterface: TitleEscrowInterface.V5,
        provider: signer.provider
      })
    ]);
  }
  const Contract = getEthersContractFromProvider(signer.provider);
  let titleEscrowContract;
  if (isV5TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v5Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  } else if (isV4TT) {
    titleEscrowContract = new Contract(
      titleEscrowAddress,
      v4Contracts.TitleEscrow__factory.abi,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      signer
    );
  }
  try {
    const isV6 = isV6EthersProvider(signer.provider);
    const args = isV5TT ? [newBeneficiaryAddress, encryptedRemarks] : [newBeneficiaryAddress];
    if (isV6) {
      await titleEscrowContract.nominate.staticCall(...args);
    } else {
      await titleEscrowContract.callStatic.nominate(...args);
    }
  } catch (e) {
    console.error("callStatic failed:", e);
    throw new Error("Pre-check (callStatic) for nominate failed");
  }
  const txOptions = await getTxOptions(signer, chainId, maxFeePerGas, maxPriorityFeePerGas);
  if (isV5TT) {
    return await titleEscrowContract.nominate(newBeneficiaryAddress, encryptedRemarks, txOptions);
  } else if (isV4TT) {
    return await titleEscrowContract.nominate(newBeneficiaryAddress, txOptions);
  }
}, "nominate");

export { nominate, transferBeneficiary, transferHolder, transferOwners };
